import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Routes, Route, Navigate} from 'react-router-dom';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from "react-redux";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './App.scss';

import {MOBILE_VIEW_BREAKPOINT, API} from './shared/constants';
import {mainConfig} from "./shared/mainConfig";
import Login from "./pages/Login/Login";
import Shop from "./pages/Shop/Shop";
import Checkout from "./pages/Checkout/Checkout";
import CheckoutResult from "./pages/CheckoutResult/CheckoutResult";
import FindId from "./pages/FindId/FindId";
import NotFound from "./pages/NotFound/NotFound";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import CheckoutHub from "./pages/CheckoutHub/CheckoutHub";
import {clearStorageUserData} from "./shared/helpers";
import Maintenance from "./pages/Maintenance/Maintenance";
import {logUserOut, logUserIn, setLanguageConfig, updateWindowSize} from "./redux/rootReducer";

const Background = API.url + 'background/shop?v=' + Date.now();

function App() {
    const {
        isLogged,
        isInMaintenance,
        selectedOffer,
        hasPurchaseIntention
    } = useSelector((state) => state.rootReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    let isFirstRender = useRef(true);
    const [searchParams] = useSearchParams();
    const [config, setConfig] = useState(mainConfig.JP);
    const [windowSize, setWindowSize] = useState({});
    const [player, setPlayer] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState('JP');

    const handleResize = useCallback(() => {
        let vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        const size = {
            width: window.innerWidth,
            height: window.innerHeight,
            isMobileView: window.innerWidth <= MOBILE_VIEW_BREAKPOINT
        };

        dispatch(updateWindowSize(size));
        setWindowSize(size);
    }, []);

    const logout = (pId, pName) => {
        clearStorageUserData();
        dispatch(logUserOut());
        if (pId && pName) {
            navigate(`/?pid=${pId}&pname=${pName}`);
        } else {
            navigate('/');
        }
    };

    useEffect(() => {
        handleResize();

        window.addEventListener('resize', handleResize, false);

        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [handleResize]);

    useEffect(() => {
        let storedPlayer = localStorage.getItem('shishiPlayerInfo');
        storedPlayer = JSON.parse(storedPlayer);
        const isOnLoginScreen = window.location.pathname === '/';
        if (!isOnLoginScreen && (!storedPlayer || (!storedPlayer.userId || !storedPlayer.username))) {
            logout();
        }
        if (!isLogged && storedPlayer && storedPlayer.userId && storedPlayer.username) {
            dispatch(logUserIn());
            setPlayer(storedPlayer);
        }
        if (isLogged && storedPlayer && !player.userId && !player.username) {
            setPlayer(storedPlayer);
        }
    });

    useEffect(() => {
        dispatch(setLanguageConfig(mainConfig[currentLanguage]));
        setConfig(mainConfig[currentLanguage]);
    }, [currentLanguage]);

    useEffect(() => {
        if (isFirstRender.current) {
            const giftSucceess = searchParams.get('gs');
            const giftFail = searchParams.get('gf');

            if (giftSucceess) {
                toast.success(config.checkoutResult.successTitle);
            }

            if (giftFail) {
                toast.error(config.checkoutResult.errorTitle);
            }
            isFirstRender.current = false;
        }

    }, [searchParams]);

    const loginComponent = <Login
        setPlayer={setPlayer}
        config={config}
        windowSize={windowSize}
        logout={logout}
    />

    return (
        <React.Fragment>
            <img className='background-master' src={Background} alt='' />
            <ToastContainer position="top-left" theme="dark" autoClose={10000} />
            {isLogged && !isInMaintenance ?
                <Header
                    currentLanguage={currentLanguage}
                    setCurrentLanguage={setCurrentLanguage}
                    windowSize={windowSize}
                    config={config}
                    player={player}
                    logout={logout}
                />
                :
                null
            }
            {!isInMaintenance ?
                <Routes>
                    <Route path={'/'} element={loginComponent}/>
                    <Route index element={loginComponent}/>
                    <Route path={'find-id'} element={<FindId config={config}/>}/>
                    <Route path={'shop'} element={
                        <Shop
                            windowSize={windowSize}
                            config={config}
                            setPlayer={setPlayer}
                            logout={logout}
                        />
                    }/>
                    <Route path={'checkout'} element={
                        !selectedOffer && !hasPurchaseIntention ?
                            <Navigate to="/shop" replace />
                            :
                            <Checkout config={config} logout={logout} />
                    }/>
                    <Route path={'checkout-hub'} element={
                        !selectedOffer && !hasPurchaseIntention ?
                            <Navigate to="/shop" replace />
                            :
                            <CheckoutHub config={config} windowSize={windowSize} />
                    }/>
                    <Route path={'checkout-result'} element={<CheckoutResult config={config}/>}/>
                    { /* 404 */}
                    <Route path={'*'} element={<NotFound config={config}/>}/>
                </Routes>
                :
                <Maintenance/>
            }
            <Footer config={config} windowSize={windowSize} isLoginPage={!isLogged} />
        </React.Fragment>
    );
}

export default App;
